import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { DragEvent, FC, lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';

import { AllPins } from 'APP/main/ChatView/AllPins/AllPins';
import { ChatView } from 'APP/main/ChatView/ChatView';
import { MainInfoBlock } from 'APP/main/MainInfoBlock/MainInfoBlock';
import { AuthorizedOngoingCall } from 'APP/main/OngoingCall/AuthorizedOngoingCall';
import { RightSidebarManager } from 'APP/main/RightSidebarManager/RightSidebarManager';
import { useChatBackgroundClassName } from 'APP/main/components/ChatBackground/hooks/useChatBackgroundClassName';
import { MediaGallery } from 'APP/main/components/MediaGallery/MediaGallery';
import { Features } from 'APP/packages/features/Features';
import { RouterLink } from 'APP/router/constants';
import Entities from 'STORE';

import { GlobalVideoPlayer } from './GlobalVideoPlayer/GlobalVideoPlayer';
import { IncomingCall } from './IncomingCall/IncomingCall';
import LiveStreaming from './LiveStreaming';
import { useMainViewPresenter } from './MainView.presenter';
import styles from './MainView.styles.m.css';
import { Modules } from './Modules/Modules';
import { Popups } from './Popups';
import { SidebarPanel } from './SidebarPanel/SidebarPanel';
import { useChatBackgroundStyles } from './components/ChatBackground/hooks/useChatBackgroundStyles';

const Catalog = lazy(async () => {
  const { Catalog } = await import('./Catalog/Catalog');
  return { default: Catalog };
});

const SpaceView = lazy(async () => {
  const { SpaceView } = await import('./SpaceView/SpaceView');
  return { default: SpaceView };
});

const Newsfeed = lazy(async () => {
  const { Newsfeed } = await import('./Newsfeed/Newsfeed');
  return { default: Newsfeed };
});

const Hub = lazy(async () => {
  const { Hub } = await import('./Hub/Hub');
  return { default: Hub };
});

const InAppView = lazy(async () => {
  const { InAppView } = await import('APP/main/InAppView/InAppView');
  return { default: InAppView };
});

const ArticlesDraft = lazy(async () => {
  const { ArticlesDraft } = await import('./ArticlesDraft/ArticlesDraft');
  return { default: ArticlesDraft };
});

const Calendar = lazy(async () => {
  const { Calendar } = await import('./Calendar/Calendar');
  return { default: Calendar };
});

const prevent = (e: DragEvent<HTMLDivElement>): void => {
  e.stopPropagation();
  e.preventDefault();
};

export const MainView: FC = observer(() => {
  const navigate = useNavigate();
  const chatBackgroundClassName = useChatBackgroundClassName();
  const { chatBackgroundStyles } = useChatBackgroundStyles();

  const presenter = useMainViewPresenter();

  const allPinsPaths = [RouterLink.AllChatPins, RouterLink.AllChannelPins];
  const chatViewPaths = [RouterLink.Chat, `${RouterLink.Channel}/*`];

  if (Features.isScheduledMessagesEnabled()) {
    chatViewPaths.push(RouterLink.Schedule);
  }

  if (Features.isThreadsEnabled()) {
    chatViewPaths.push(RouterLink.Thread);
  }

  return (
    <>
      <div
        className={classNames(styles.container, chatBackgroundClassName)}
        onDrop={prevent}
        onDragOver={prevent}
        style={chatBackgroundStyles}
      >
        <SidebarPanel />

        <Routes>
          <Route
            path={RouterLink.Main}
            element={<MainInfoBlock />}
          />
          {presenter.isCalendarEnabled && (
            <Route
              path={RouterLink.Calendar}
              element={
                <Suspense fallback={null}>
                  <Calendar />
                </Suspense>
              }
            />
          )}
          {Features.isCatalogEnabled() && (
            <Route
              path={RouterLink.Catalog}
              element={
                <Suspense fallback={null}>
                  <Catalog />
                </Suspense>
              }
            />
          )}
          {Features.isNewsfeedEnabled() && (
            <Route
              path={RouterLink.Newsfeed}
              element={
                <Suspense fallback={null}>
                  <Newsfeed />
                </Suspense>
              }
            />
          )}
          {Features.isHubEnabled() && <Route path={RouterLink.Hub} />}
          {Features.isMarketplaceEnabled() && <Route path={RouterLink.Market} />}
          {Features.isSpacesEnabled() && (
            <Route
              path={`${RouterLink.Space}/*`}
              element={
                <Suspense fallback={null}>
                  <SpaceView />
                </Suspense>
              }
            />
          )}
          {Features.isArticleEnabled() && (
            <Route
              path={`${RouterLink.Channel}/article-drafts`}
              element={
                <Suspense fallback={null}>
                  <ArticlesDraft />
                </Suspense>
              }
            />
          )}

          {allPinsPaths.map((path) => (
            <Route
              key={path}
              path={path}
              element={<AllPins />}
            />
          ))}
          {chatViewPaths.map((path) => (
            <Route
              key={path}
              path={path}
              element={<ChatView />}
            />
          ))}

          <Route
            path="*"
            element={
              <Navigate
                to={{
                  pathname: RouterLink.Main,
                }}
                replace
              />
            }
          />
        </Routes>
        <MediaGallery />
        {Features.isHubEnabled() && (
          <Suspense fallback={null}>
            <Hub />
          </Suspense>
        )}

        {(Features.isHubEnabled() || Features.isMarketplaceEnabled()) && (
          <Suspense fallback={null}>
            <InAppView goBack={(): void => navigate(-1)} />
          </Suspense>
        )}
        {!!Entities.Calls.activeCall && (
          <AuthorizedOngoingCall mainCall={Entities.Calls.activeCall} />
        )}
        <RightSidebarManager />
      </div>

      <Modules />
      <Popups />
      <IncomingCall />
      <LiveStreaming />
      <GlobalVideoPlayer />
    </>
  );
});
